import type { MessageBlock, ThreadMessage } from "@rakazo/contracts";
import { messageReaction } from "./message-reactions.js";

type PresentableMessage = {
  id: string;
  threadId?: string;
  runId?: string;
  role?: string;
  audience?: ThreadMessage["audience"];
  replyToMessageId?: string | null;
  blocks: readonly MessageBlock[];
};

/** Keep the newest current-state Site card per thread and Site, without changing stored history.
 * Найновіша картка поточного стану сайта в чаті; збережена історія незмінна. */
export function compactSiteResultMessages<T extends PresentableMessage>(
  messages: readonly T[],
): T[] {
  const latest = new Map<string, { message: number; block: number }>();
  const key = (message: T, block: MessageBlock) =>
    message.role === "bot" && block.kind === "site"
      ? JSON.stringify([message.threadId ?? null, block.spaceId, block.siteId])
      : null;
  messages.forEach((message, index) => {
    message.blocks.forEach((block, blockIndex) => {
      const identity = key(message, block);
      if (identity) latest.set(identity, { message: index, block: blockIndex });
    });
  });
  return messages.flatMap((message, index) => {
    const blocks = message.blocks.filter((block, blockIndex) => {
      const identity = key(message, block);
      const selected = identity ? latest.get(identity) : undefined;
      return !selected || (selected.message === index && selected.block === blockIndex);
    });
    if (blocks.length === message.blocks.length) return [message];
    return blocks.length ? [{ ...message, blocks }] : [];
  });
}

export type UserVisibleMessagesOptions = {
  /**
   * Keep `bot_message_sent` / `bot_message_received` rows as compact chips; peer replies fold
   * under the received chip. Without it, bot-to-bot traffic (and the files it carried) is dropped
   * and bot-addressed peer replies are hidden: the person's chat shows only what is meant for them.
   */
  includePeerReceipts?: boolean;
  /** Peer-run ids from `run.trigger === "bot_message"` when receipts may be out of window. */
  knownPeerRunIds?: Iterable<string>;
  /**
   * Run ids for background triggers (e.g. `tickets`). Their messages are hidden
   * except cards that need user input.
   */
  backgroundRunIds?: Iterable<string>;
  /**
   * Surface a peer-run's own `text` reply (e.g. a delegating bot's summary to the user)
   * alongside `ask` cards. Defaults to true for chat-thread rendering; set false for
   * contexts like sidebar previews that should stay ask-only and never echo peer chatter.
   */
  includeDelegatedReplyText?: boolean;
};

export function isPeerReceiptBlocks(blocks: readonly MessageBlock[]): boolean {
  return blocks.some(
    (block) => block.kind === "bot_message_sent" || block.kind === "bot_message_received",
  );
}

/** Bot-to-bot traffic: a direct peer message or a group handoff.
 * Обмін між ботами: пряме повідомлення іншому ботові або передача в групі. */
export function isPeerTrafficBlock(block: MessageBlock): boolean {
  return (
    block.kind === "bot_message_sent" ||
    block.kind === "bot_message_received" ||
    block.kind === "handoff"
  );
}

/**
 * True when nothing in the message is for the person: peer traffic, the files it carried and tool
 * activity. Such a message is never news (unread) and is addressed to a bot.
 * Нічого для людини: обмін ботів, його файли й службова активність.
 */
export function carriesOnlyPeerTraffic(blocks: readonly MessageBlock[]): boolean {
  return (
    blocks.some(isPeerTrafficBlock) &&
    blocks.every(
      (block) =>
        isPeerTrafficBlock(block) ||
        block.kind === "file" ||
        block.kind === "image" ||
        !isPeerConversationBlock(block),
    )
  );
}

/**
 * The part of a message meant for the person: peer traffic goes, and so do the files it carried
 * (a peer message stores its attachments beside the peer block). Undefined when nothing is left.
 * Частина для людини: обмін між ботами прибирається разом із файлами, які він ніс.
 */
export function withoutPeerTraffic<T extends PresentableMessage>(message: T): T | undefined {
  if (!message.blocks.some(isPeerTrafficBlock)) return message;
  const blocks = message.blocks.filter(
    (block) => !isPeerTrafficBlock(block) && block.kind !== "file" && block.kind !== "image",
  );
  return blocks.length ? { ...message, blocks } : undefined;
}

/** Conversation bodies/cards are distinct from tool activity, regardless of who started a run.
 * Текст і картки діалогу відрізняються від службової активності незалежно від ініціатора. */
export function isPeerConversationBlock(block: unknown): boolean {
  return (
    !!block &&
    typeof block === "object" &&
    "kind" in block &&
    typeof block.kind === "string" &&
    block.kind !== "steps" &&
    block.kind !== "progress" &&
    block.kind !== "meta"
  );
}

/** User-input cards remain visible when their run otherwise stays in the background. */
export function isBackgroundInputBlock(block: unknown): boolean {
  return (
    !!block &&
    typeof block === "object" &&
    "kind" in block &&
    (block.kind === "ask" ||
      block.kind === "computer" ||
      block.kind === "mcp_approval" ||
      block.kind === "app_connect")
  );
}

/** Cards the person acts on or takes part in (asks, approvals, takeovers, connections, calls):
 * they stay in the person's chat whichever run wrote them and whoever it answered.
 * Картки, на які людина відповідає або в яких бере участь, завжди видимі. */
export function isPersonActionBlock(block: MessageBlock): boolean {
  return (
    isBackgroundInputBlock(block) ||
    block.kind === "choice" ||
    block.kind === "connect" ||
    block.kind === "call_request" ||
    block.kind === "voice_call"
  );
}

function isAskBlock(block: MessageBlock): boolean {
  return block.kind === "ask";
}

export type FoldedTranscript<T> = {
  messages: T[];
  /** Peer-run replies keyed by the id of the `bot_message_received` marker they fold under. */
  folded: ReadonlyMap<string, readonly T[]>;
  /** The marker each folded message folds under, so a jump can expand it. */
  markerOf: ReadonlyMap<string, string>;
};

/**
 * Drop peer-run activity; optionally keep sent/received receipt rows. With receipts kept, a
 * only positively bot-addressed replies fold; human and legacy unknown replies stay inline.
 * Згортаються лише відповіді з відомим адресатом-ботом; людські й старі невідомі видимі.
 */
export function foldPeerReplies<T extends PresentableMessage>(
  messages: readonly T[],
  options: UserVisibleMessagesOptions = {},
): FoldedTranscript<T> {
  const includePeerReceipts = options.includePeerReceipts === true;
  const markerByRunId = new Map<string, string>();
  for (const message of messages) {
    if (
      message.runId &&
      !markerByRunId.has(message.runId) &&
      message.blocks.some((block) => block.kind === "bot_message_received")
    ) {
      markerByRunId.set(message.runId, message.id);
    }
  }
  const peerRunIds = new Set([...(options.knownPeerRunIds ?? []), ...markerByRunId.keys()]);
  const backgroundRunIds = new Set(options.backgroundRunIds ?? []);
  const includeText = options.includeDelegatedReplyText !== false;
  const folded = new Map<string, T[]>();
  const markerOf = new Map<string, string>();
  const fold = (markerId: string, message: T) => {
    folded.set(markerId, [...(folded.get(markerId) ?? []), message]);
    markerOf.set(message.id, markerId);
    return false;
  };
  // Bot-to-bot messages hidden from the person, so a reaction to one goes with it.
  const peerHidden = new Set<string>();
  const hidePeer = (message: T) => {
    peerHidden.add(message.id);
    return false;
  };

  const keep = (message: T): boolean => {
    if (
      !includePeerReceipts &&
      message.replyToMessageId &&
      peerHidden.has(message.replyToMessageId) &&
      message.role !== undefined &&
      messageReaction({ ...message, role: message.role, blocks: [...message.blocks] })
    )
      return hidePeer(message);
    if (includeText && message.blocks.some(isPersonActionBlock)) return true;
    if (includeText && message.audience === "user") return true;
    if (message.runId && backgroundRunIds.has(message.runId)) {
      return message.blocks.some(isBackgroundInputBlock);
    }
    if (isPeerReceiptBlocks(message.blocks)) return includePeerReceipts;
    // A reaction folds with its folded parent so the count shows when the parent is expanded.
    const parentMarkerId = message.replyToMessageId
      ? markerOf.get(message.replyToMessageId)
      : undefined;
    if (
      parentMarkerId !== undefined &&
      message.role !== undefined &&
      messageReaction({ ...message, role: message.role, blocks: [...message.blocks] })
    )
      return fold(parentMarkerId, message);
    // Output addressed to another bot is bot-to-bot traffic, whether or not its run's receipt is
    // loaded; cards the person has to act on stay.
    if (!includePeerReceipts && message.role === "bot" && message.audience === "bot")
      return hidePeer(message);
    if (!message.runId || !peerRunIds.has(message.runId)) return true;
    // User-input cards stay inline; sidebar previews keep only asks, as before.
    if (message.blocks.some(includePeerReceipts || includeText ? isPersonActionBlock : isAskBlock))
      return true;
    if (!includeText || !message.blocks.some(isPeerConversationBlock)) return false;
    // Only bot replies fold: a person's answer, steering or reaction in the run stays inline.
    if (message.role !== "bot") return true;
    if (message.audience !== "bot") return true;
    if (
      !message.blocks.some(
        (block) => block.kind === "text" || block.kind === "file" || block.kind === "image",
      )
    )
      return false;
    const markerId = includePeerReceipts ? markerByRunId.get(message.runId) : undefined;
    // A reply whose marker is outside the loaded page stays inline.
    if (markerId === undefined) return true;
    return fold(markerId, message);
  };
  // In output addressed to another bot only the cards the person acts on are theirs; the text and
  // files beside them stay bot-to-bot. / У відповіді іншому ботові людині належать лише картки.
  const personCardsOnly = (message: T): T =>
    message.role === "bot" &&
    message.audience === "bot" &&
    message.blocks.some(isPersonActionBlock) &&
    !message.blocks.every(isPersonActionBlock)
      ? { ...message, blocks: message.blocks.filter(isPersonActionBlock) }
      : message;
  const visible = messages.flatMap((original): T[] => {
    const stripped = includePeerReceipts ? original : withoutPeerTraffic(original);
    const message = stripped && !includePeerReceipts ? personCardsOnly(stripped) : stripped;
    if (!message) {
      hidePeer(original);
      return [];
    }
    return keep(message) ? [message] : [];
  });
  return { messages: compactSiteResultMessages(visible), folded, markerOf };
}

export function userVisibleMessages<T extends PresentableMessage>(
  messages: readonly T[],
  options: UserVisibleMessagesOptions = {},
): T[] {
  return foldPeerReplies(messages, options).messages;
}

/** The newest bot message the person sees, as they see it: what read-aloud may speak.
 * Найновіше видиме людині повідомлення бота, у тому вигляді, як вона його бачить. */
export function latestVisibleBotMessage<T extends PresentableMessage>(
  messages: readonly T[],
): T | undefined {
  return userVisibleMessages(messages).findLast((message) => message.role === "bot");
}

/** The transcript with each expanded marker's folded replies drawn right after it. */
export function expandPeerReplies<T extends PresentableMessage>(
  transcript: FoldedTranscript<T>,
  expandedMarkerIds: ReadonlySet<string>,
): T[] {
  if (expandedMarkerIds.size === 0) return transcript.messages;
  return transcript.messages.flatMap((message) =>
    expandedMarkerIds.has(message.id)
      ? [message, ...(transcript.folded.get(message.id) ?? [])]
      : [message],
  );
}
